/**
 * ArtifactSandbox - Fábrica de iframes seguros para renderizar artifacts.
 *
 * Cada sandbox recebe um ID único (sandboxId) que é injetado no documento
 * do iframe. Todas as mensagens postMessage incluem esse ID, então o listener
 * no parent só aceita mensagens do seu próprio iframe — sem cross-artifact leakage.
 *
 * Segurança:
 *   - sandbox="allow-scripts" por padrão (null origin, sem acesso ao parent)
 *   - allowSameOrigin=true adiciona allow-same-origin (apenas Python/Pyodide, que precisa de sessionStorage)
 *   - postMessage filtrado por sandboxId único
 *
 * CSP:
 *   iframes com srcdoc herdam o CSP da página pai (spec HTML). O BookStack usa
 *   'strict-dynamic' que ignora http:/https: e exige nonce em todo <script>.
 *   Lemos o nonce da página pai e o injetamos em todos os <script> do srcdoc.
 */

export interface SandboxOptions {
    html: string;
    minHeight?: number;
    maxHeight?: number;
    autoResize?: boolean;
    allowSameOrigin?: boolean;

    /** Quando true, injeta CSS no documento do iframe para mesclar com a bolha do chat.
     *  Usa bgColor como background de html/body; sem ele cai em transparent. */
    seamless?: boolean;
    /** Cor de background da bolha AI configurada pelo usuário (ex: '#2a2a2a').
     *  Usada no modo seamless para que o documento interno do iframe tenha exatamente
     *  a mesma cor da bolha, evitando bordas brancas ou contrastes indesejados. */
    bgColor?: string;
}

export class ArtifactSandbox {
    private iframe: HTMLIFrameElement;
    private sandboxId: string;
    private nonce: string;
    private messageHandler: ((event: MessageEvent) => void) | null = null;
    private readyHandler: ((event: MessageEvent) => void) | null = null;

    constructor(private options: SandboxOptions) {
        this.sandboxId = `sb-${Math.random().toString(36).slice(2, 10)}`;

        this.nonce = '';
        const scripts = document.querySelectorAll<HTMLScriptElement>('script');
        for (const s of scripts) {
            const n = s.nonce || s.getAttribute('nonce') || '';
            if (n) { this.nonce = n; break; }
        }

        this.iframe = document.createElement('iframe');
        this.iframe.className = 'artifact-sandbox-iframe';
        this.iframe.setAttribute('sandbox', options.allowSameOrigin ? 'allow-scripts allow-same-origin' : 'allow-scripts');
        this.iframe.setAttribute('loading', 'lazy');
        this.iframe.setAttribute('title', 'Artifact Preview');
        // Começa invisível (opacity:0) mas em fluxo normal para que o load event interno
        // do iframe dispare — display:none impede o carregamento e bloqueia o postMessage
        // de artifact-loaded. O skeleton fica como overlay absoluto por cima (ver ArtifactContainer).
        this.iframe.style.cssText = 'width:100%;border:none;background:transparent;opacity:0;transition:opacity 0.25s ease;';
        this.iframe.style.minHeight = `${options.minHeight ?? 200}px`;

        this.iframe.srcdoc = this.buildDocument(options.html);
    }

    /**
     * Monta o iframe no parent e inicia os listeners de resize e ready.
     * @param parent   Elemento onde o iframe será inserido
     * @param onReady  Callback chamado quando o iframe sinalizar que está pronto
     */
    mount(parent: HTMLElement, onReady?: () => void): void {
        parent.appendChild(this.iframe);

        if (this.options.autoResize !== false) {
            this.messageHandler = (event: MessageEvent) => {
                if (event.data?.sandboxId !== this.sandboxId) return;
                if (event.data.type === 'artifact-resize' && typeof event.data.height === 'number') {
                    this.iframe.style.height = `${event.data.height}px`;
                }
            };
            window.addEventListener('message', this.messageHandler);
        }

        if (onReady) {
            this.readyHandler = (event: MessageEvent) => {
                if (event.data?.type === 'artifact-loaded' && event.data.sandboxId === this.sandboxId) {
                    window.removeEventListener('message', this.readyHandler!);
                    this.readyHandler = null;
                    onReady();
                }
            };
            window.addEventListener('message', this.readyHandler);
        }
    }

    /** Torna o iframe visível após o carregamento (chamado pelo ArtifactContainer). */
    reveal(): void {
        // iframe nunca foi display:none, só opacity:0 — força reflow e aplica fade-in
        void this.iframe.offsetHeight;
        this.iframe.style.opacity = '1';
    }

    /** Substitui o conteúdo do iframe (hot-reload do editor). */
    updateContent(html: string): void {
        this.iframe.srcdoc = this.buildDocument(html);
    }

    getElement(): HTMLIFrameElement {
        return this.iframe;
    }

    destroy(): void {
        if (this.messageHandler) {
            window.removeEventListener('message', this.messageHandler);
            this.messageHandler = null;
        }
        if (this.readyHandler) {
            window.removeEventListener('message', this.readyHandler);
            this.readyHandler = null;
        }
        this.iframe.remove();
    }

    // -------------------------------------------------------------------------
    // Construção do documento
    // -------------------------------------------------------------------------

    /**
     * Envolve o HTML gerado pelo renderer com:
     *   1. Script que expõe window.__sandboxId (usado pelos renderers para postMessage)
     *   2. Script de auto-resize + sinal de "pronto"
     *
     * Todos os <script> recebem o nonce da página pai para satisfazer o CSP 'strict-dynamic'.
     */
    private buildDocument(html: string): string {
        const id = this.sandboxId;
        const n  = this.nonce;
        const na = n ? ` nonce="${n}"` : '';  // atributo nonce para injetar nos scripts
        const minH = this.options.minHeight ?? 200;
        const maxHClamp = this.options.maxHeight ? `measured = Math.min(measured, ${this.options.maxHeight});` : '';

        // Exposto como global para que renderers (ex: Python) usem no postMessage
        const idScript = `<script${na}>window.__sandboxId = "${id}";</script>`;

        // Resize + sinal de ready — injeta UMA vez pelo sandbox (renderers não precisam duplicar)
        const resizeScript = `<script${na}>
(function () {
    var ID = "${id}";
    function notifyResize() {
        var b = document.body, d = document.documentElement;
        var measured = Math.max(b.scrollHeight, b.offsetHeight, d.scrollHeight, d.offsetHeight, ${minH});
        ${maxHClamp}
        window.parent.postMessage({ type: 'artifact-resize', height: measured, sandboxId: ID }, '*');
    }
    if (typeof ResizeObserver !== 'undefined') {
        new ResizeObserver(notifyResize).observe(document.body);
    }
    window.addEventListener('load', function () {
        notifyResize();
        window.parent.postMessage({ type: 'artifact-loaded', sandboxId: ID }, '*');
    });
    setTimeout(notifyResize, 300);
    setTimeout(notifyResize, 1000);
    setTimeout(notifyResize, 3000);
})();
</script>`;

        const isFullDoc = /<html[\s>]/i.test(html);
        const bg = this.options.bgColor || 'transparent';
        const seamlessStyle = this.options.seamless
            ? `<style>html,body{background:${bg}!important;margin:0!important;padding:0!important;}</style>`
            : '';

        if (isFullDoc) {
            let result = html;

    
            if (n) {
                result = result.replace(/<script(\s|>)/gi, `<script nonce="${n}"$1`);
            }

           
            result = result.replace(/<head>/i, `<head>${idScript}${seamlessStyle}`);
            if (/<\/body>/i.test(result)) {
                result = result.replace(/<\/body>/i, `${resizeScript}</body>`);
            } else {
                result += resizeScript;
            }
            return result;
        }

        const bodyPadding = this.options.seamless ? '' : 'body { padding: 16px; }';
        return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
${idScript}
${seamlessStyle}
<style>
*, *::before, *::after { box-sizing: border-box; }
html, body { margin: 0; padding: 0; font-family: system-ui, -apple-system, sans-serif; }
${bodyPadding}
</style>
</head>
<body>
${html}
${resizeScript}
</body>
</html>`;
    }
}
